import {
  camelCase,
  extend,
  get,
  includes,
  isEmpty,
  isUndefined,
  reduce,
  set,
  upperFirst,
} from 'lodash';
import {
  DealerCashRuleType,
  DisplayFormat,
  FinanceType,
  OfferCategoryType,
} from './legos/constants';
import {
  fmtMoney,
  fmtPercent,
  fmtPropNameForTaxUpdate,
  fmtTaxes,
} from './formatter';
import { FeeCategory } from './constants';
import { Overrides, Product } from './pen-utils';
import { getDealerCashRuleAppliedValue } from './payment-utils';
import { Pencil } from './legos/pencilTypes';
import { Bundle } from './legos/penTypes';

const EXCLUDE_LIST = ['freight'];

/**
 * Builds the group name (header) of the change log entry of an offer.
 * @param pencil
 * @param financeType
 * @param offerIndex
 * @returns
 */
export const getGroupName = (
  pencil: Pencil,
  financeType: FinanceType,
  offerIndex: number,
) => {
  if (financeType === FinanceType.CASH) {
    return FinanceType.CASH;
  }
  const months = get(pencil, `data.${financeType}[${offerIndex}].months`);
  const mileage = get(pencil, `data.${financeType}[${offerIndex}].mileage`);
  const monthToken = months ? `${months} months ` : '';
  const mileageToken = mileage ? `${mileage} miles` : '';
  return `${monthToken}${mileageToken}`.trim();
};

class ChangeLog {
  data: any = {};
  pencil: any;
  resetPencilHasBeenTriggered = false;

  constructor(pencil: any, data?: any) {
    if (data) {
      this.data = data;
    }
    this.pencil = pencil;
  }

  setResetPencilHasBeenTriggered() {
    this.resetPencilHasBeenTriggered = true;
  }

  getLogs() {
    if (this.resetPencilHasBeenTriggered && isEmpty(this.data)) {
      return { resettedAllChanges: [] };
    }
    return this.data;
  }

  private getKeyName(offerIndex: number, financeType: FinanceType) {
    // Will only potentially return something on Finance offers
    const categoryType: OfferCategoryType = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].category.type`,
    );
    const keyName = `${financeType}${upperFirst(categoryType)}`;
    return keyName;
  }

  private updateOffer(
    financeType: FinanceType,
    offerIndex: number,
    groupName: string,
    propName: string,
    oldValue: any,
    newValue: any,
  ) {
    const keyName = this.getKeyName(offerIndex, financeType);
    set(
      this.data,
      [`${keyName}Terms`, `${offerIndex}`, 'groupName'],
      groupName,
    );
    set(
      this.data,
      [`${keyName}Terms`, `${offerIndex}`, 'updates', propName, 'old'],
      oldValue,
    );
    set(
      this.data,
      [`${keyName}Terms`, `${offerIndex}`, 'updates', propName, 'new'],
      newValue,
    );
  }

  private updateFeesHelper(
    propName: string,
    fees: any[],
    financeType?: FinanceType,
  ) {
    if (!isEmpty(fees)) {
      const newFees = reduce(
        fees,
        (acc: any, item) => {
          // eslint-disable-next-line no-param-reassign
          acc[camelCase(item.name)] = {
            new: item.value ? fmtMoney(item.value) : fmtPercent(item.apr),
          };
          return acc;
        },
        {},
      );
      if (financeType) {
        set(this.data, [propName, 'groupName'], financeType);
      }
      set(this.data, [propName, 'updates'], newFees);
    }
  }

  updatePrice(financeType: FinanceType, pencil: any, newValue: number) {
    const oldValue = get(this.pencil, `data.${financeType}.0.price`);
    set(
      this.data,
      [financeType, 'updates', 'vehiclePrice', 'old'],
      fmtMoney(oldValue),
    );
    set(
      this.data,
      [financeType, 'updates', 'vehiclePrice', 'new'],
      fmtMoney(newValue),
    );
  }

  updateDiscount(financeType: FinanceType, pencil: any, newValue: number) {
    const oldValue = get(this.pencil, `data.${financeType}.0.discount`);
    set(
      this.data,
      [financeType, 'updates', 'discount', 'old'],
      fmtMoney(oldValue),
    );
    set(
      this.data,
      [financeType, 'updates', 'discount', 'new'],
      fmtMoney(newValue),
    );
  }

  // private
  private updateOfferMoneyProp(
    field: string,
    financeType: FinanceType,
    newValue: number,
  ) {
    const oldValue = get(this.pencil, `data.${financeType}.0.${field}`);
    set(this.data, [financeType, 'updates', field, 'old'], fmtMoney(oldValue));
    set(this.data, [financeType, 'updates', field, 'new'], fmtMoney(newValue));
  }

  // update a property indexed under offers
  updateOfferProperty(
    propName: string,
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
  ) {
    switch (propName) {
      case 'apr':
        return this.updateApr(pencil, offerIndex, newValue);
      case 'rebate':
        return this.updateRebate(financeType, pencil, offerIndex, newValue);
      case 'dealerCash':
        return this.updateDealerCash(financeType, pencil, offerIndex, newValue);
      case 'months':
        return this.updateTerm(financeType, offerIndex, newValue);
      case 'residual':
        return this.updateResidual(pencil, offerIndex, newValue);
      case 'mileage':
        return this.updateMileage(pencil, offerIndex, newValue);
      case 'rate':
        return this.updateRate(pencil, offerIndex, newValue);
      case 'onePaymentRate':
        return this.updateOnePaymentRate(pencil, offerIndex, newValue);
      case 'programs':
        return this.updateIncentives(financeType, pencil, offerIndex, newValue);
      case 'discount':
      case 'price':
        return this.updateOfferMoneyProp(propName, financeType, newValue);
      default:
        throw new Error('Unknown Offer Prop');
    }
  }

  updateTaxes(
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
    taxType: string,
    taxName: string,
    prop: string,
  ) {
    const groupName = getGroupName(pencil, financeType, offerIndex);
    const oldValue =
      get(
        this.pencil,
        `data.${financeType}[${offerIndex}].taxBreakdown.${taxType}.${prop}`,
      ) || '';

    this.updateOffer(
      financeType,
      offerIndex,
      groupName,
      `${taxName} ${prop}`,
      fmtTaxes(oldValue, prop),
      fmtTaxes(newValue, prop),
    );
  }

  updateApr(pencil: any, offerIndex: number, newValue: number) {
    const term = get(
      pencil,
      `data.${FinanceType.PURCHASE}[${offerIndex}].months`,
    );
    const oldValue = get(
      this.pencil,
      `data.${FinanceType.PURCHASE}[${offerIndex}].apr`,
    );
    this.updateOffer(
      FinanceType.PURCHASE,
      offerIndex,
      `${term} months`,
      'apr',
      fmtPercent(oldValue),
      fmtPercent(newValue),
    );
  }

  updateRebate(
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
  ) {
    if (financeType === FinanceType.CASH) {
      this.updateCashRebate(newValue);
      return;
    }
    const propName = fmtPropNameForTaxUpdate(financeType, pencil, offerIndex);
    const groupName = getGroupName(pencil, financeType, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].rebate`,
    );
    this.updateOffer(
      financeType,
      offerIndex,
      groupName,
      propName,
      fmtMoney(oldValue),
      fmtMoney(newValue),
    );
  }

  updateDealerCash(
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
  ) {
    const groupName = getGroupName(pencil, financeType, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].dealerCash`,
    );
    this.updateOffer(
      financeType,
      offerIndex,
      groupName,
      'dealerCash',
      fmtMoney(oldValue),
      fmtMoney(newValue),
    );

    // if the dealer cash rule that applies dealer cash to price uses percent
    // update the change log value for that as well
    const dealerCashRule = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].dealerCashRule`,
    );
    if (dealerCashRule?.type === DealerCashRuleType.PRICE_OFFSET_PERCENT) {
      const newAppliedDealerCashValue = getDealerCashRuleAppliedValue({
        ...(get(pencil, `data.${financeType}[${offerIndex}]`) || {}),
        dealerCash: newValue,
      });
      this.updateDealerCashRule(
        financeType,
        pencil,
        offerIndex,
        newAppliedDealerCashValue,
      );
    }
  }

  updateDealerCashRule(
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
  ) {
    const groupName = getGroupName(pencil, financeType, offerIndex);
    const oldValue = getDealerCashRuleAppliedValue(
      get(this.pencil, `data.${financeType}[${offerIndex}]`),
    );
    this.updateOffer(
      financeType,
      offerIndex,
      groupName,
      'dealerCashAppliedToPrice',
      fmtMoney(oldValue),
      fmtMoney(newValue),
    );
  }

  updateTerm(financeType: FinanceType, offerIndex: number, newValue: number) {
    const oldValue = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].months`,
    );
    this.updateOffer(
      financeType,
      offerIndex,
      `${newValue} months`,
      'term',
      `${oldValue} months`,
      `${newValue} months`,
    );
  }

  updateResidual(pencil: any, offerIndex: number, newValue: number) {
    const groupName = getGroupName(pencil, FinanceType.LEASE, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${FinanceType.LEASE}[${offerIndex}].residual`,
    );
    this.updateOffer(
      FinanceType.LEASE,
      offerIndex,
      groupName,
      'residual',
      fmtMoney(oldValue),
      fmtMoney(newValue),
    );
  }

  updateMileage(pencil: any, offerIndex: number, newValue: number) {
    const groupName = getGroupName(pencil, FinanceType.LEASE, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${FinanceType.LEASE}[${offerIndex}].mileage`,
    );
    this.updateOffer(
      FinanceType.LEASE,
      offerIndex,
      groupName,
      'mileage',
      fmtMoney(oldValue),
      fmtMoney(newValue),
    );
  }

  updateRate(pencil: any, offerIndex: number, newValue: number) {
    const groupName = getGroupName(pencil, FinanceType.LEASE, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${FinanceType.LEASE}[${offerIndex}].rate`,
    );
    this.updateOffer(
      FinanceType.LEASE,
      offerIndex,
      groupName,
      'rate',
      fmtPercent(oldValue),
      fmtPercent(newValue),
    );
  }

  updateOnePaymentRate(pencil: any, offerIndex: number, newValue: number) {
    const groupName = getGroupName(pencil, FinanceType.LEASE, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${FinanceType.LEASE}[${offerIndex}].onePaymentRate`,
    );
    this.updateOffer(
      FinanceType.LEASE,
      offerIndex,
      groupName,
      'onePaymentRate',
      fmtPercent(oldValue),
      fmtPercent(newValue),
    );
  }

  updateIncentives(
    financeType: FinanceType,
    pencil: any,
    offerIndex: number,
    newValue: number,
  ) {
    const groupName = getGroupName(pencil, financeType, offerIndex);
    const oldValue = get(
      this.pencil,
      `data.${financeType}[${offerIndex}].programs`,
    );
    this.updateOffer(
      financeType,
      offerIndex,
      groupName,
      'programs',
      oldValue,
      newValue,
    );
  }

  updatePencilOfferProp(args: {
    pencil: Pencil;
    financeType: FinanceType;
    offerIndex: number;
    propName: string;
    newValue: number;
    format: DisplayFormat;
  }) {
    const groupName = getGroupName(
      args.pencil,
      args.financeType,
      args.offerIndex,
    );
    const oldValue = get(
      this.pencil,
      `data.${args.financeType}[${args.offerIndex}].${args.propName}`,
    );
    const formatter =
      args.format === DisplayFormat.MONEY ? fmtMoney : fmtPercent;
    this.updateOffer(
      args.financeType,
      args.offerIndex,
      groupName,
      args.propName,
      formatter(oldValue),
      formatter(args.newValue),
    );
  }

  // update fields that are not separated by finance type
  updateProp(propName: string, newValue: number) {
    if (includes(EXCLUDE_LIST, propName)) {
      return;
    }
    const oldValue = get(this.pencil, `data.${propName}`);
    // If value is boolean, display it as a string, else convert to money amount
    if (typeof newValue === 'boolean') {
      set(this.data, [propName, 'updates', propName, 'old'], `${oldValue}`);
      set(this.data, [propName, 'updates', propName, 'new'], `${newValue}`);
    } else {
      set(
        this.data,
        [propName, 'updates', propName, 'old'],
        fmtMoney(oldValue),
      );
      set(
        this.data,
        [propName, 'updates', propName, 'new'],
        fmtMoney(newValue),
      );
    }
  }

  updateCashRebate(newValue: number) {
    const oldValue = get(this.pencil, 'data.cash[0].rebate');
    set(
      this.data,
      [`${FinanceType.CASH}`, 'updates', 'rebate', 'old'],
      fmtMoney(oldValue),
    );
    set(
      this.data,
      [`${FinanceType.CASH}`, 'updates', 'rebate', 'new'],
      fmtMoney(newValue),
    );
  }

  updateFees(newFees: any, financeType?: FinanceType) {
    this.updateFeesHelper(
      'taxableFees',
      newFees[FeeCategory.TAXABLE],
      financeType,
    );
    this.updateFeesHelper(
      'nonTaxableFees',
      newFees[FeeCategory.NON_TAXABLE],
      financeType,
    );
    this.updateFeesHelper(
      'capitalizedCostFees',
      newFees[FeeCategory.CAPITALIZED_COST],
      financeType,
    );
    this.updateFeesHelper(
      'capitalizedCostTaxableFees',
      newFees[FeeCategory.CAPITALIZED_COST_TAXABLE],
      financeType,
    );
  }

  updateAddOns(addOns: any, financeType?: FinanceType) {
    if (!isEmpty(addOns)) {
      const newAddOns = reduce(
        addOns,
        (acc: any, item) => {
          // eslint-disable-next-line no-param-reassign
          acc[camelCase(item.description)] = { new: fmtMoney(item.price) };
          return acc;
        },
        {},
      );
      if (financeType) {
        set(this.data, ['addOns', 'groupName'], financeType);
      }
      set(this.data, ['addOns', 'updates'], newAddOns);
    }
  }

  updateProtectionPlans(
    financeType: FinanceType,
    bundles: Bundle[],
    products: Product[],
    oldOverrides: Overrides,
    newOverrides: Overrides,
    allProducts: Product[],
  ) {
    if (!isEmpty(bundles)) {
      set(
        this.data,
        [financeType, 'updates', 'selectedBundles', 'new'],
        bundles.map(p => p.name).join(', '),
      );
    }
    if (!isEmpty(products)) {
      set(
        this.data,
        [financeType, 'updates', 'selectedProducts', 'new'],
        products.map(p => p.displayName).join(', '),
      );
    }

    const allOverrides = extend({}, oldOverrides, newOverrides);
    // Gets all the price changes made by the overrides
    if (Object.keys(allOverrides).length > 0) {
      const priceChange = Object.keys(allOverrides)
        // eslint-disable-next-line consistent-return, array-callback-return
        .map(id => {
          const oldValue = get(oldOverrides, [id]);
          const newValue = get(newOverrides, [id]);
          const changedProduct = allProducts.filter(p => p.id === id)[0];
          const productName = get(changedProduct, 'displayName');

          // Logs existing override price to a new override price
          if (newValue && oldValue && newValue !== oldValue) {
            return `${productName} changed from ${fmtMoney(
              oldOverrides[id],
            )} to ${fmtMoney(newOverrides[id])}`;
          }

          // Logs existing override price to not having an override anymore
          if (oldValue && isUndefined(newValue)) {
            return `${productName} changed from ${fmtMoney(
              oldOverrides[id],
            )} to default price`;
          }

          // Logs non-existing override to having an override
          if (isUndefined(oldValue) && newValue) {
            return `${productName} changed from default to ${fmtMoney(
              newOverrides[id],
            )}`;
          }
        })
        .filter(item => item !== undefined);

      if (priceChange.length > 0) {
        set(
          this.data,
          [financeType, 'updates', 'priceChange', 'new'],
          priceChange.join(', '),
        );
      }
    }
  }

  updateAccessories(accessories: any) {
    if (!isEmpty(accessories)) {
      const accessoryMap = reduce(
        accessories,
        (acc, item) => ({ ...acc, [item.name]: { new: fmtMoney(item.price) } }),
        {},
      );
      set(this.data, ['accessories', 'updates'], accessoryMap);
    }
  }

  updateDaysToFirstPayment(newValue: number) {
    const oldValue = get(this.pencil, 'data.daysToFirstPayment');
    if (oldValue) {
      set(
        this.data,
        ['daysToFirstPayment', 'updates', 'daysToFirstPayment', 'old'],
        `${oldValue} days`,
      );
    }
    set(
      this.data,
      ['daysToFirstPayment', 'updates', 'daysToFirstPayment', 'new'],
      `${newValue} days`,
    );
  }

  updateTradeInMileage(tradeInIndex: number, newValue: number) {
    const oldValue = get(
      this.pencil,
      `data.pencilTradeIns[${tradeInIndex}].mileage`,
    );

    set(
      this.data,
      ['Trade In Mileage', 'updates', 'Trade In Mileage', 'old'],
      oldValue,
    );
    set(
      this.data,
      ['Trade In Mileage', 'updates', 'Trade In Mileage', 'new'],
      newValue,
    );
  }
}

export default ChangeLog;
